export const mapCargosToOptions = (cargos = []) => {
  if (!Array.isArray(cargos)) return []

  return cargos
    .map((cargo) => ({
      value: String(cargo.TB_ID || cargo.id || ''),
      label: cargo.TB_DESCRIPCION || cargo.descripcion || '',
    }))
    .filter((opt) => opt.value && opt.label)
}

export const mapAreasToOptions = (areas = []) => {
  if (!Array.isArray(areas)) return []

  return areas
    .map((area) => ({
      value: String(area.TB_ID || area.id || ''),
      label: area.TB_DESCRIPCION || area.descripcion || '',
    }))
    .filter((opt) => opt.value && opt.label)
}

/**
 * Mapea el tipo de teléfono del frontend (descripción) al ID del backend (number)
 * Según el mapeo inverso en useDatosPersonales.js:
 * - Fijo: IDs 1-5 (usamos 1 como valor por defecto)
 * - Celular: IDs 6-8 (usamos 7 como valor por defecto)
 * @param {string} tipoTelefono - Tipo de teléfono ('Celular', 'Fijo', o vacío)
 * @returns {number|null} ID del tipo de teléfono para el backend, o null si está vacío
 */
export const mapTipoTelefonoToId = (tipoTelefono) => {
  // Si el valor está vacío o es 'Seleccionar', retornar null
  if (!tipoTelefono || tipoTelefono.trim() === '' || tipoTelefono.toLowerCase() === 'seleccionar') {
    return null
  }

  const TIPO_TELEFONO_MAP = {
    celular: 7, // ID para celular
    fijo: 1, // ID para fijo
  }

  // Normalizar a minúsculas para la comparación
  const tipoNormalizado = tipoTelefono.toLowerCase().trim()
  return TIPO_TELEFONO_MAP[tipoNormalizado] || null
}

/**
 * Construye el payload para actualizar datos personales según el formato del backend
 * @param {Object} formData - Datos del formulario
 * @param {number} idUsuario - ID del usuario
 * @returns {Object} Payload formateado para el backend
 */
export const buildDatosPersonalesPayload = (formData, idUsuario) => {
  const tipoTelPrincipal = mapTipoTelefonoToId(formData.tipoTelefonoPrincipal)
  const tipoTelAlternativo = mapTipoTelefonoToId(formData.tipoTelefonoAlternativo)

  const payload = {
    idUsuario: Number(idUsuario),
    apellido: formData.apellido?.trim() || '',
    nombre: formData.nombre?.trim() || '',
  }

  // Solo incluir teléfono principal si tiene tipo válido
  if (tipoTelPrincipal !== null) {
    payload.tipoTelefono = tipoTelPrincipal
    payload.telefono = formData.numeroTelefonoPrincipal?.trim() || ''
  }

  // Solo incluir teléfono alternativo si tiene tipo válido y número
  if (tipoTelAlternativo !== null && formData.numeroTelefonoAlternativo?.trim()) {
    payload.tipoTelefonoAlt = tipoTelAlternativo
    payload.telefonoAlt = formData.numeroTelefonoAlternativo.trim()
  }

  return payload
}

/**
 * Construye el payload para registrar la solicitud de asociación de nueva cuenta
 * Endpoint: POST /registro-usuario/v1/solicitudes?validaMail=false
 * @param {Object} formData - Datos ingresados en el modal
 * @param {string} formData.cuit - CUIT con formato XX-XXXXXXXX-X
 * @param {string} formData.cargoId - ID del cargo seleccionado
 * @param {string} formData.areaAfinidadId - ID del área de afinidad seleccionada
 * @param {Object} datosPersonales - Datos del usuario logueado (de useDatosPersonales)
 * @param {string} datosPersonales.correoElectronico - Email del usuario
 * @param {string} datosPersonales.apellido - Apellido del usuario
 * @param {string} datosPersonales.nombre - Nombre del usuario
 * @param {number|null} datosPersonales.tipoTelefonoPrincipalId - ID numérico del tipo de teléfono
 * @param {string} datosPersonales.numeroTelefonoPrincipal - Número de teléfono principal
 * @param {string} emailUsuarioAlta - Email del usuario que da de alta (sesión)
 * @returns {Object} Payload formateado para el backend
 */
export const buildAsociarCuentaPayload = (formData, datosPersonales, emailUsuarioAlta) => {
  return {
    email: datosPersonales.correoElectronico || '',
    // El endpoint espera el CUIT sin guiones
    cuit: (formData.cuit || '').replace(/-/g, ''),
    apellido: datosPersonales.apellido || '',
    nombre: datosPersonales.nombre || '',
    idCargo: String(formData.cargoId || ''),
    idAreaAfinidad: String(formData.areaAfinidadId || ''),
    tipoTelefono: String(datosPersonales.tipoTelefonoPrincipalId ?? ''),
    numeroTelefono: datosPersonales.numeroTelefonoPrincipal || '',
    aceptoTerminos: 'S',
    usuarioAlta: emailUsuarioAlta || datosPersonales.correoElectronico || '',
    // Origen de la solicitud (PBI 83986): siempre 'C' = asociar cuenta en Mi Perfil.
    origenSolicitud: 'C',
  }
}
